import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropType from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { PAGE_CODES } from 'constants/pageCodes';
import useLoanId from 'hooks/useLoanId';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { openDialogAction } from 'store/global/actions';
import {
  cancelSendingBundleAction,
  getLoanBundleHistoryAction,
} from 'store/loanApplications/actions';

import { UnderwritingTestIds } from '../../../../Underwriting.testIds';
import CancelSendingBundlePopup from '../cancelSendingBundlePopup';
import { getStyles } from '../loanBundleHistoryTable/LoanBundleHistoryTable.styles';

import { getColumns } from './ScheduledBundleHistoryTable.constants';

const ScheduledBundleHistoryTable = ({ generatedDate = '', formBundleId, setIsLoading }) => {
  const theme = useTheme();

  const dispatch = useDispatch();

  // Cancelling a scheduled send DELETEs /loan/{loanId}/cancel-sending-bundle, which the server gates
  // on LoanEditorPolicy.Edit = { loanEditor.edit } alone — the `.access` alias getPagePermissions
  // folds into isEditAllowed is NOT accepted there, so this uses the strict flag (DEV-5614).
  const { isStrictEditAllowed: isCancelSendingAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_APPLICATION),
  );

  const columns = useMemo(
    () => getColumns({ enableCancel: isCancelSendingAllowed }),
    [isCancelSendingAllowed],
  );

  const classes = getStyles(theme, { columns });

  const { applicationId } = useParams();

  const loanId = useLoanId();

  const scheduledBundleData = { status: 'Pending', generatedDate };

  const cancelSendingBundle = useCallback(async () => {
    setIsLoading(true);

    return dispatch(cancelSendingBundleAction({ loanId, formBundleId }))
      .then(() => dispatch(getLoanBundleHistoryAction({ loanId, applicationId })))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [setIsLoading, dispatch, loanId, formBundleId, applicationId]);

  const showCancelSendingBundlePopup = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Confirm bundle cancellation',
        body: dialogProps => (
          <CancelSendingBundlePopup
            {...dialogProps}
            date={generatedDate}
            onConfirm={cancelSendingBundle}
          />
        ),
      }),
    );
  }, [cancelSendingBundle, dispatch, generatedDate]);

  return (
    <div className="w-full flex flex-col">
      <Box className="flex items-center justify-end p-16" sx={classes.header}>
        <Button
          disabled
          data-testid={UnderwritingTestIds.SCHEDULED_BUNDLE_HISTORY_VIEW_DOCUMENTS_BUTTON}
        >
          View Documents
        </Button>
      </Box>

      <TableContainer>
        <Table stickyHeader sx={classes.table}>
          <TableHead>
            <TableRow className="h-64 uppercase">
              {columns.map(({ align, id, disablePadding, label, tooltip, minWidthClassName }) => (
                <TableCell
                  align={align}
                  className={minWidthClassName || 'min-w-200'}
                  key={id}
                  padding={disablePadding ? 'none' : 'default'}
                >
                  {!!tooltip && (
                    <ButtonWithTooltip
                      data-testid={UnderwritingTestIds.getScheduledBundleHistoryInfoId(id)}
                      icon={InfoIcon}
                      iconButtonClassName="mb-1"
                      iconSize={IconSize[14]}
                      size="small"
                      title={tooltip}
                    />
                  )}
                  <span>{label}</span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow hover className="h-64">
              {columns.map(({ id, align, format }) => (
                <TableCell align={align} component="th" key={id} scope="row">
                  {format
                    ? format({
                        value: scheduledBundleData[id],
                        onCancelButtonClick: showCancelSendingBundlePopup,
                      })
                    : scheduledBundleData[id]}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

ScheduledBundleHistoryTable.propTypes = {
  formBundleId: PropType.string.isRequired,
  generatedDate: PropType.string,
  ...WithLoadingPropTypes,
};

export default withLoading(ScheduledBundleHistoryTable);
